import { apiFetch } from "./apiFetch";

export type PartnerStatus = "Active" | "Inactive";

export type PartnerLinkPlatform =
  | "Website"
  | "Instagram"
  | "Facebook"
  | "Twitter"
  | "LinkedIn"
  | "YouTube"
  | "TikTok"
  | "Discourse"
  | "Other";

export interface PartnerLink {
  platform: PartnerLinkPlatform;
  url: string;
  label?: string;
  iconUrl?: string;
}

export interface Partner {
  id: string;
  groupId: string;
  displayName: string;
  hashtag: string;
  aboutText?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  links?: PartnerLink[];
  discourseGroupName?: string;
  discourseCategoryUrl?: string;
  status: PartnerStatus;
  // Per-partner transactional-email toggles (ENG-895). Nullable: absent ⇒ send.
  sendWelcomeEmail?: boolean;
  sendOffboardingEmail?: boolean;
  /**
   * Stable session keys marked as this partner's "preferred" sessions (ENG-990).
   * Each entry is a meeting's stable key (recurringMeetingId ?? gcalEventId ?? id).
   */
  preferredSessionIds?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePartnerRequest {
  displayName: string;
  hashtag: string;
  aboutText?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  links?: PartnerLink[];
  discourseGroupName?: string;
  discourseCategoryUrl?: string;
  sendWelcomeEmail?: boolean;
  sendOffboardingEmail?: boolean;
}

export type UpdatePartnerRequest = Partial<CreatePartnerRequest> & {
  status?: PartnerStatus;
  /**
   * Full replacement of the partner's preferred session keys. Absent ⇒ leave
   * unchanged; sending the array REPLACES the stored list. Cannot be combined
   * with the add/remove intents below.
   */
  preferredSessionIds?: string[];
  /**
   * Add/remove intents applied SERVER-SIDE against the stored list — prefer
   * these over the full replacement so a stale client snapshot can't drop
   * keys other admins added meanwhile.
   */
  addPreferredSessionIds?: string[];
  removePreferredSessionIds?: string[];
};

interface PartnersListResponse {
  partners: Partner[];
}

export interface AssetUploadUrlRequest {
  fileName: string;
  contentType: string;
}

export interface AssetUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  s3Key: string;
}

export interface PartnerMember {
  appUserId: string;
  email: string;
  displayName: string;
  revenueCatId: string;
  /** Account creation date (ISO 8601); "" when unknown. */
  createdAt: string;
}

export type ProvisionStatus = "created" | "attached" | "already_member";

export interface ProvisionResult {
  email: string;
  status: ProvisionStatus;
  appUserId: string;
  revenueCatId: string;
  alreadyHadActiveSubscription: boolean;
}

export interface OffboardResult {
  email: string;
  appUserId: string;
  revenueCatId: string;
  rcRevoked: boolean;
  userGroupDeleted: boolean;
  tagDeleted: boolean;
}

export interface BulkRowResult {
  email: string;
  displayName: string;
  status: ProvisionStatus | "error";
  appUserId?: string;
  alreadyHadActiveSubscription?: boolean;
  error?: string;
}

export interface BulkImportSummary {
  total: number;
  created: number;
  attached: number;
  alreadyMember: number;
  error: number;
  alreadyHadActiveSubscription: number;
}

export interface BulkImportResponse {
  summary: BulkImportSummary;
  results: BulkRowResult[];
}

interface PartnerMembersResponse {
  members: PartnerMember[];
}

/** Identifier refs of a meeting scoped to a partner (partner-only). */
export interface ScopedMeetingRef {
  externalMeetingId: string;
  gcalEventId: string | null;
  recurringMeetingId: string | null;
}

interface ScopedMeetingsResponse {
  meetings: ScopedMeetingRef[];
}

// Small in-memory cache for the partner list — it's read on every meeting's
// Partner Association section/read-out, changes rarely, and is invalidated on
// any create/update below.
let cachedPartners: { at: number; data: Partner[] } | null = null;
const PARTNERS_CACHE_TTL_MS = 60_000;
const invalidatePartnersCache = () => {
  cachedPartners = null;
};

// Per-partner cache for member rosters. The details and members dialogs both
// re-read the whole roster on open; it only changes through the add/remove/bulk
// calls below, each of which invalidates its partner's entry.
const cachedMembers = new Map<string, { at: number; data: PartnerMember[] }>();
const MEMBERS_CACHE_TTL_MS = 60_000;
const invalidateMembersCache = (partnerId: string) => {
  cachedMembers.delete(partnerId);
};

// Per-partner cache for scoped-meeting refs (calendar Partner filter/badges).
// Short TTL as a safety net; the meeting edit dialog also invalidates it
// explicitly (via updateMeeting) since scope changes happen there, not through
// any PartnerResource call.
const cachedScopedMeetings = new Map<
  string,
  { at: number; promise: Promise<ScopedMeetingRef[]> }
>();
const SCOPED_MEETINGS_CACHE_TTL_MS = 30_000;

/** Drop every partner's scoped-meetings cache — a meeting's partner scope
 * changed and the old owner is unknown here. */
export const invalidateScopedMeetingsCache = () => {
  cachedScopedMeetings.clear();
};

export const PartnerResource = {
  getList: () =>
    apiFetch<PartnersListResponse>("/admin/partners").then((r) => r.partners),

  /** getList with a short in-memory cache (invalidated on create/update). */
  getListCached: async (): Promise<Partner[]> => {
    if (
      cachedPartners &&
      Date.now() - cachedPartners.at < PARTNERS_CACHE_TTL_MS
    ) {
      return cachedPartners.data;
    }
    const data = await PartnerResource.getList();
    cachedPartners = { at: Date.now(), data };
    return data;
  },

  listMembers: async (partnerId: string): Promise<PartnerMember[]> => {
    const cached = cachedMembers.get(partnerId);
    if (cached && Date.now() - cached.at < MEMBERS_CACHE_TTL_MS) {
      return cached.data;
    }
    const { members } = await apiFetch<PartnerMembersResponse>(
      `/admin/partner/${partnerId}/members`,
    );
    cachedMembers.set(partnerId, { at: Date.now(), data: members });
    return members;
  },

  addMember: (
    partnerId: string,
    data: { email: string; displayName: string; password?: string },
  ) =>
    apiFetch<ProvisionResult>(`/admin/partner/${partnerId}/member`, {
      method: "POST",
      body: data,
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return r;
    }),

  removeMember: (
    partnerId: string,
    ref: { appUserId?: string; email?: string },
  ) =>
    apiFetch<OffboardResult>(`/admin/partner/${partnerId}/member`, {
      method: "DELETE",
      body: ref,
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return r;
    }),

  bulkImport: (partnerId: string, csv: string) =>
    apiFetch<BulkImportResponse>(`/admin/partner/${partnerId}/members/bulk`, {
      method: "POST",
      body: { csv },
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return r;
    }),

  /** Meetings scoped to this partner (partner-only), as identifier refs.
   * Cached + in-flight-deduped per partner. */
  getScopedMeetingsCached: (partnerId: string): Promise<ScopedMeetingRef[]> => {
    const hit = cachedScopedMeetings.get(partnerId);
    if (hit && Date.now() - hit.at < SCOPED_MEETINGS_CACHE_TTL_MS) {
      return hit.promise;
    }
    const promise = apiFetch<ScopedMeetingsResponse>(
      `/admin/partner/${partnerId}/scoped-meetings`,
    ).then((r) => r.meetings);
    cachedScopedMeetings.set(partnerId, { at: Date.now(), promise });
    // Never cache a rejection — drop it so the next read retries.
    promise.catch(() => {
      if (cachedScopedMeetings.get(partnerId)?.promise === promise) {
        cachedScopedMeetings.delete(partnerId);
      }
    });
    return promise;
  },

  getById: (partnerId: string) =>
    apiFetch<Partner>(`/admin/partner/${partnerId}`),

  create: (data: CreatePartnerRequest) =>
    apiFetch<Partner>("/admin/partner", { method: "POST", body: data }).then(
      (r) => {
        invalidatePartnersCache();
        return r;
      },
    ),

  update: (partnerId: string, data: UpdatePartnerRequest) =>
    apiFetch<Partner>(`/admin/partner/${partnerId}`, {
      method: "PATCH",
      body: data,
    }).then((r) => {
      invalidatePartnersCache();
      return r;
    }),

  getAssetUploadUrl: (partnerId: string, data: AssetUploadUrlRequest) =>
    apiFetch<AssetUploadUrlResponse>(
      `/admin/partner/${partnerId}/asset-upload-url`,
      { method: "POST", body: data },
    ),
};

/**
 * Upload a logo/asset for an existing partner: request a presigned URL, PUT the
 * file straight to S3, and return the public URL to persist as logoUrl.
 */
export const uploadPartnerAsset = async (
  partnerId: string,
  file: File,
): Promise<string> => {
  const { uploadUrl, publicUrl } = await PartnerResource.getAssetUploadUrl(
    partnerId,
    { fileName: file.name, contentType: file.type },
  );

  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Asset upload failed: ${response.status}`);
  }

  return publicUrl;
};
